import axios, { AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';

// Store reference - will be set when pinia is initialized
let loaderStore: any = null;

// Counter for pending requests
let pendingRequests = 0;

// Minimum loading time to prevent flashing
const MIN_LOADING_TIME = 300;
let loadingStartTime = 0;

// URLs to exclude from loader (e.g., background polling)
const excludedUrls: string[] = [
  // Add URLs that shouldn't trigger the loader
];

// Check if URL should trigger loader
function shouldShowLoader(url: string | undefined): boolean {
  if (!url) return true;
  return !excludedUrls.some(excluded => url.includes(excluded));
}

// Request interceptor
const requestInterceptor = (config: InternalAxiosRequestConfig) => {
  // Let Laravel translate validation/API messages (e.g. MajorSeason errors)
  try {
    const lang =
      typeof localStorage !== "undefined"
        ? localStorage.getItem("selectedLanguageCode") || "en"
        : "en";
    const code = String(lang).toLowerCase().split("-")[0];
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>)["Accept-Language"] =
      code === "ar" ? "ar" : "en";
  } catch {
    /* ignore */
  }

  if (shouldShowLoader(config.url)) {
    pendingRequests++;
    
    if (pendingRequests === 1) {
      loadingStartTime = Date.now();
      if (loaderStore) {
        loaderStore.startLoading('Loading data...');
      }
    }
  }
  
  return config;
};

// Request error handler
const requestErrorHandler = (error: AxiosError) => {
  pendingRequests = Math.max(0, pendingRequests - 1);
  
  if (pendingRequests === 0 && loaderStore) {
    const elapsed = Date.now() - loadingStartTime;
    const remaining = Math.max(0, MIN_LOADING_TIME - elapsed);
    
    setTimeout(() => {
      loaderStore.stopLoading();
    }, remaining);
  }
  
  return Promise.reject(error);
};

// Response interceptor
const responseInterceptor = (response: AxiosResponse) => {
  if (shouldShowLoader(response.config.url)) {
    pendingRequests = Math.max(0, pendingRequests - 1);
    
    if (pendingRequests === 0 && loaderStore) {
      const elapsed = Date.now() - loadingStartTime;
      const remaining = Math.max(0, MIN_LOADING_TIME - elapsed);
      
      setTimeout(() => {
        loaderStore.stopLoading();
      }, remaining);
    }
  }
  
  return response;
};

// Response error handler
const responseErrorHandler = (error: AxiosError) => {
  if (shouldShowLoader(error.config?.url)) {
    pendingRequests = Math.max(0, pendingRequests - 1);
    
    if (pendingRequests === 0 && loaderStore) {
      const elapsed = Date.now() - loadingStartTime;
      const remaining = Math.max(0, MIN_LOADING_TIME - elapsed);
      
      setTimeout(() => {
        loaderStore.stopLoading();
      }, remaining);
    }
  }
  
  return Promise.reject(error);
};

// Setup function to be called after pinia is initialized
export function setupAxiosLoader(store: any) {
  loaderStore = store;
  
  // Add interceptors
  axios.interceptors.request.use(requestInterceptor, requestErrorHandler);
  axios.interceptors.response.use(responseInterceptor, responseErrorHandler);
}

// Force stop loader (useful for navigation guards)
export function forceStopLoader() {
  pendingRequests = 0;
  if (loaderStore) {
    loaderStore.forceStop();
  }
}

// Manually trigger loader (for non-axios operations)
export function showLoader(text: string = 'Loading...') {
  if (loaderStore) {
    loaderStore.startLoading(text);
  }
}

// Manually hide loader
export function hideLoader() {
  if (loaderStore) {
    loaderStore.stopLoading();
  }
}

// Add URL to exclusion list
export function excludeUrlFromLoader(url: string) {
  if (!excludedUrls.includes(url)) {
    excludedUrls.push(url);
  }
}

// Remove URL from exclusion list
export function includeUrlInLoader(url: string) {
  const index = excludedUrls.indexOf(url);
  if (index > -1) {
    excludedUrls.splice(index, 1);
  }
}

